import React, { useState, useEffect, useCallback } from 'react';
import { Box, Grid, CircularProgress, Alert } from '@mui/material';
import FrameworkCard from './FrameworkCard';
import FrameworkFilters, { type FrameworkFiltersState } from './FrameworkFilters';
import { ApiService, ApiEndpoints } from '../../../services/ApiService';
import { transformFrameworks } from '../../../utils/dataTransform';
import { useCompany } from '../../../contexts/CompanyContext';

/**
 * Framework interface
 */
interface Framework {
  id: string | number;
  name: string;
  description?: string;
  status: 'active' | 'draft' | 'archived';
  version?: string;
  createdAt: string;
  updatedAt: string;
}


/**
 * Framework List Component
 * 
 * Displays a list of compliance frameworks with filtering options
 * Fetches data from the API using the ApiService
 */
export default function FrameworkList() {
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FrameworkFiltersState>({
    search: '',
    status: 'all'
  });
  
  // Get company context to ensure proper data loading
  const { currentCompany, loading: companyLoading } = useCompany();

  /**
   * Fetch frameworks from API
   */
  const fetchFrameworks = useCallback(async () => {
    // Don't fetch if company is still loading
    if (companyLoading) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Small delay to ensure company context is properly set
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const response = await ApiService.get<any[]>(ApiEndpoints.FRAMEWORKS.LIST);
      const transformed = transformFrameworks(response);
      setFrameworks(transformed);
    } catch (err) {
      console.error('Error fetching frameworks:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      
      // Fallback to mock data for development
      setFrameworks([
        {
          id: 1,
          name: 'ISO 27001',
          description: 'Information security management standard',
          status: 'active',
          version: '2022',
          createdAt: '2023-01-15T00:00:00Z',
          updatedAt: '2023-01-15T00:00:00Z'
        },
        {
          id: 2,
          name: 'SOC 2',
          description: 'Security, availability, processing integrity, confidentiality, and privacy',
          status: 'active',
          version: 'Type 2',
          createdAt: '2023-02-10T00:00:00Z',
          updatedAt: '2023-02-10T00:00:00Z'
        },
        {
          id: 3,
          name: 'GDPR',
          description: 'General Data Protection Regulation',
          status: 'draft',
          createdAt: '2023-03-05T00:00:00Z',
          updatedAt: '2023-03-05T00:00:00Z'
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [companyLoading]);

  /**
   * Fetch frameworks on mount and when company changes
   */
  useEffect(() => {
    fetchFrameworks();
  }, [fetchFrameworks, currentCompany?.id]);

  /**
   * Filter frameworks based on filter criteria
   */
  const filteredFrameworks = frameworks.filter(framework => {
    const matchesSearch = !filters.search || 
      framework.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      (framework.description && framework.description.toLowerCase().includes(filters.search.toLowerCase()));
    
    const matchesStatus = filters.status === 'all' || framework.status === filters.status;
    
    return matchesSearch && matchesStatus;
  });

  /**
   * Handle filter changes
   */
  const handleFilterChange = (newFilters: Partial<FrameworkFiltersState>) => {
    setFilters({ ...filters, ...newFilters });
  };

  /**
   * Loading state
   */
  if (loading || companyLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  /**
   * Error state
   */
  if (error && frameworks.length === 0) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading frameworks: {error}
        </Alert>
        <Alert severity="info" sx={{ mb: 2 }}>
          Tip: Try refreshing the page or navigating away and back.
        </Alert>
      </Box>
    );
  }

  /**
   * Render component
   */
  return (
    <Box data-testid="framework-list-container">
      <FrameworkFilters filters={filters} onFilterChange={handleFilterChange} />
      
      {filteredFrameworks.length === 0 ? (
        <Alert severity="info" data-testid="framework-list-empty">
          {frameworks.length === 0 
            ? "No frameworks have been created yet. Click 'New Framework' to add your first framework."
            : "No frameworks match the current filters"
          }
        </Alert>
      ) : (
        <Grid container spacing={3} data-testid="framework-grid">
          {filteredFrameworks.map(framework => (
            <Grid item xs={12} sm={6} md={4} key={framework.id}>
              <FrameworkCard framework={framework} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
